import React, { useState } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import PublicIcon from "@mui/icons-material/Public";
import SmsIcon from "@mui/icons-material/Sms";
import MDButton from "components/MDButton";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import CallIcon from "@mui/icons-material/Call";
import LinkIcon from "@mui/icons-material/Link";
import EditIcon from "@mui/icons-material/Edit";
import DeleteDialog from "components/UIComponents/DeleteDialog";
import CircularIndeterminate from "components/MDLoading";
import EditUrlInput from "./TComponents/EditUrlInput";
import { useMaterialUIController } from "context";
import { useDispatch, useSelector } from "react-redux";
import { deleteQRCode, updateQRCodeURL } from "store/slice/qrCode/qrCodeSlice";
import { Icon, Tooltip } from "@mui/material";
import { generateRandomString } from "global/functions";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";

const QRCodeCard = (props) => {
  const [controller] = useMaterialUIController();
  const { sidenavColor, darkMode } = controller;
  const roleModule = useSelector(
    (state) => state.users?.meInfo?.roleId?.modules,
  );
  const { item, updateParentData } = props;
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [qrCodeId, setQrCodeId] = useState("");
  const [url, setUrl] = useState("");

  const handleDownload = (bucketUrl) => {
    const fileName = generateRandomString();
    const anchor = document.createElement("a");
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.href = bucketUrl;
    anchor.download = fileName;
    anchor.click();
    document.body.removeChild(anchor);
  };

  const handleDeleteQR = () => {
    setLoading(true);
    dispatch(deleteQRCode({ qrCodeId }))
      .unwrap()
      .then((res) => {
        if (res?.success) {
          updateParentData();
          setLoading(false);
          setDialogOpen(false);
        }
      })
      .catch((err) => {
        console.error("Error deleting QR code:", err);
      });
  };

  const handleUpdateQRCodeURL = () => {
    setLoading(true);
    dispatch(updateQRCodeURL({ id: qrCodeId, url }))
      .unwrap()
      .then((res) => {
        if (res?.success) {
          updateParentData();
          setLoading(false);
          setEditDialogOpen(false);
        }
      })
      .catch((err) => {
        setLoading(false);
        setEditDialogOpen(false);
        console.error("Error updating QR code URL:", err);
      });
  };

  return (
    <Card
      elevation={4}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        position: "relative",
        p: 1,
        bgcolor: darkMode ? "grey.900" : "grey.50",
        transition: "0.3s",
      }}
    >
      {/* Top Right Type Chip */}
      <Chip
        color="secondary"
        size="small"
        label={item.type === "web" ? "Web" : "SMS"}
        icon={item.type === "web" ? <PublicIcon /> : <SmsIcon />}
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          fontWeight: "bold",
          px: 1,
        }}
      />

      <CardContent>
        <Grid container spacing={3} alignItems="center">
          {/* QR Image */}
          <Grid item xs={12} md={3}>
            <Avatar
              variant="rounded"
              src={item.qrCodeImage}
              alt={item.title}
              sx={{
                width: 120,
                height: 120,
                borderRadius: 3,
                boxShadow: 4,
                bgcolor: "primary.lighter",
              }}
            />
          </Grid>

          {/* Details */}
          <Grid item xs={12} md={9}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {item.title}
                </Typography>
                <Icon fontSize="small">
                  {item.type === "web" ? <PublicIcon /> : <SmsIcon />}
                </Icon>
              </Stack>

              {/* URL / Mobile */}
              <Typography
                variant="body2"
                sx={{ display: "flex", alignItems: "center", opacity: 0.8 }}
              >
                {item.type === "web" ? (
                  <Box
                    component="a"
                    href={
                      item.url.startsWith("http")
                        ? item.url
                        : `https://${item.url}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      color: darkMode ? "grey.300" : "grey.800",
                      textDecoration: "none",
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        color: `${sidenavColor}.main`,
                        textDecoration: "underline",
                        transform: "translateX(3px)",
                        opacity: 1,
                      },
                    }}
                  >
                    <LinkIcon fontSize="small" sx={{ mr: 0.5 }} />
                    {item.url}
                  </Box>
                ) : (
                  <>
                    <CallIcon fontSize="small" sx={{ mr: 0.5 }} /> {item.mobile}
                  </>
                )}
              </Typography>

              {/* SMS message */}
              {item.type === "sms" && (
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  <SmsIcon fontSize="small" sx={{ mr: 0.5 }} /> {item.message}
                </Typography>
              )}

              {/* Scan Count */}
              <Box
                sx={{
                  my: 1,
                  p: 1,
                  px: 2,
                  borderRadius: 2,
                  bgcolor: darkMode ? "grey.800" : "white",
                  boxShadow: 2,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  🔍 Scan Count:
                </Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ fontWeight: 700 }}
                >
                  {item.scanCount || 0}
                </Typography>
              </Box>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} mt={2}>
                {/* Download */}
                <Tooltip title="Download QR Code" placement="top" arrow>
                  <MDButton
                    variant="gradient"
                    color={sidenavColor}
                    onClick={() => handleDownload(item.qrCodeImage)}
                  >
                    <DownloadIcon />
                  </MDButton>
                </Tooltip>

                {/* Delete */}
                {roleModule?.QRCode_delete && (
                  <Tooltip title="Delete QR Code" placement="top" arrow>
                    <MDButton
                      variant="gradient"
                      color={sidenavColor}
                      onClick={() => {
                        setQrCodeId(item._id);
                        setDialogOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </MDButton>
                  </Tooltip>
                )}

                {/* Edit */}
                {roleModule?.QRCode_update && (
                  <Tooltip title="Edit QR Code URL" placement="top" arrow>
                    <MDButton
                      variant="gradient"
                      color={sidenavColor}
                      onClick={() => {
                        setQrCodeId(item._id);
                        setUrl(item.url);
                        setEditDialogOpen(true);
                      }}
                    >
                      <EditIcon />
                    </MDButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>

      {/* Loading */}
      <CircularIndeterminate
        type="full"
        size={20}
        text="Please wait.."
        open={loading}
      />

      {/* Delete Dialog */}
      <DeleteDialog
        dialogOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        handelClick={() => handleDeleteQR(qrCodeId)}
      />

      {/* Edit URL Dialog */}
      <EditUrlInput
        dialogOpen={editDialogOpen}
        setUrl={setUrl}
        url={url}
        onClose={() => setEditDialogOpen(false)}
        handelClick={handleUpdateQRCodeURL}
      />
    </Card>
  );
};

export default QRCodeCard;
